import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, map, Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Logger, PreviewMode, UtilityService } from 'src/app/@shared';
import { OfferPromo, PromoDomain, OfferPromoService, OfferService, Offer, OfferDomain, OfferGroupDomain } from '../..';
import { Status } from '../../models/offer-promo-status';
import { AuthorizeControlService } from 'pr1-ui-components';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { EventWorkflowService } from '../../services/event-workflow.service';

const log = new Logger('VersionPromoCardsComponent');

@Component({
  selector: 'app-version-promo-cards',
  templateUrl: './version-promo-cards.component.html',
  styleUrls: ['./version-promo-cards.component.scss'],
})
export class VersionPromoCardsComponent<TOfferPromo extends OfferPromo, TPromoDomain extends PromoDomain<OfferPromo>, T extends Offer, TOfferDomain extends OfferDomain<Offer>> implements OnInit, OnDestroy {
  Status: typeof Status = Status;
  PreviewMode = PreviewMode;
  viewModel$ = combineLatest([
    this.offerpromoService.getpromoChildVersions$,
    this.offerpromoService.isLoading$
  ]).pipe(
    map(([promos, isLoading]) => {
      this.promoVariantList = new MatTableDataSource(promos);
      return { promos, isLoading };
    }),
    catchError(error => {
      log.error('Error loading view model', error);
      return of({ promos: [], isLoading: false });
    })
  );

  promoVariantList: MatTableDataSource<OfferGroupDomain<Offer>> = new MatTableDataSource();
  selection = new SelectionModel<OfferGroupDomain<Offer>>(true, [], true, (o1, o2) => o1.DomainId === o2.DomainId);
  readOnlyMode = true;
  private subscriptions: Subscription[] = [];

  constructor(
    public utilityService: UtilityService,
    private authorizationService: AuthorizeControlService,
    public offerService: OfferService<T, TOfferDomain>,
    private router: Router,
    private route: ActivatedRoute,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    private ewService: EventWorkflowService
  ) { }

  ngOnInit(): void {
    log.debug('init');

    this.readOnlyMode = this.offerpromoService.getOfferDetailsReadOnlyValue();
    this.subscriptions.push(
      this.offerpromoService.offerDetailsReadOnlyMode$.subscribe((res) => {
        this.readOnlyMode = res;
      })
    );
  }

  ngAfterViewInit() {
    if (this.ewService.readOnlyMode != null) {
      this.readOnlyMode = this.ewService.readOnlyMode;
      return;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onImageError(event: Event): void {
    const element = event.target as HTMLImageElement;
    element.src = './assets/images/default-image.png'; // Path to your default image
  }

  onEdit(id: string) {
    this.router.navigate([`../../../${id}/promos/promodetails`], { relativeTo: this.route });
  }

  clearSelection() {
    this.selection.clear();
  }

  updateStatus(status: Status, row: OfferGroupDomain<Offer>) {
    let selectedRecordsDetails: OfferGroupDomain<Offer>[] = [JSON.parse(JSON.stringify(row))];
    this.updateRecordsStatus(selectedRecordsDetails, status);
  }

  updateRecordsStatus(selectedRecordsDetails: Array<any>, status: Status) {
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      selectedRecordsDetails = selectedRecordsDetails.map((x: any) => {
        let obj ={
          Id: x.Detail.Id,
          Label: x.EventOfferType,
          Status: status as unknown as string
        }
        return obj;
      });
      this.offerpromoService.updateStatus(selectedRecordsDetails).subscribe({
        next: () => {
          this.offerpromoService.reloadPromoVersions();
          this.clearSelection();
          this.offerpromoService.updateRecordStatus(new Date());
        },
        error: (err) => {
          log.error('Failed to update status', err);
        }
      });
    }
  }

  getSelectedSectionRecords() {
    return this.selection.selected;
  }

  isAllSelected(): boolean {
    return this.promoVariantList.data.every(promo => this.selection.isSelected(promo));
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.promoVariantList.data.forEach(promo => {
        if (!this.selection.isSelected(promo)) {
          this.selection.select(promo);
        }
      });
    } else {
      this.promoVariantList.data.forEach(promo => {
        this.selection.deselect(promo);
      });
    }
  }

  isSomeSelectedOnPage(): boolean {
    return this.promoVariantList.data.some(promo => this.selection.isSelected(promo));
  }

  toggleSelect(checked: boolean, promo: OfferGroupDomain<Offer>) {
    if (checked) {
      if (!this.selection.isSelected(promo)) {
        this.selection.select(promo);
      }
    } else {
      this.selection.deselect(promo);
    }
  }

  canEdit(): boolean {
    return this.authorizationService.checkAccess('edit||offermang') && !this.readOnlyMode;
  }

  getStatusClass(promo: OfferGroupDomain<Offer>): string {
    const status = promo?.Detail?.Status;
    return status === '0' ? 'statusPENDING' : 'status' + status;
  }

  getStatusText(promo: OfferGroupDomain<Offer>): string {
    const status = promo?.Detail?.Status;
    return status === '0' ? 'PENDING' : status || '';
  }
}
